import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePlans, getFeatureDisplayText } from '@/hooks/usePlans';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  Check,
  Crown,
  Star,
  Shield,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Calendar,
  Utensils,
  Brain,
  Smartphone,
  ChefHat,
  ArrowRight,
  Camera,
  BarChart3,
  Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';

interface Plan {
  id: string;
  name: string;
  displayName: string;
  price: number;
  originalPrice?: number;
  period: string;
  description: string;
  popular?: boolean;
  features: string[];
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  gradient: string;
  buttonText: string;
  savings?: string;
}

interface FAQ {
  question: string;
  answer: string;
}

const VirtualAssistant = () => {
  usePageTitle('Virtual Assistant - AI-Powered Menu Intelligence');

  const navigate = useNavigate();
  const location = useLocation();
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'quarterly' | 'annual'>('annual');
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  const { data: plansData, loading, error } = usePlans();

  // Auto-scroll to pricing plans section
  useEffect(() => {
    const timer = setTimeout(() => {
      const pricingSection = document.getElementById('pricing-plans');
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [location]);

  // Filter only Virtual Assistant plans (yummy, delicious, gourmet)
  const plans: Plan[] = plansData ? plansData.plans
    .filter(plan => ['yummy', 'delicious', 'gourmet'].includes(plan.name))
    .map(plan => ({
      id: plan.name,
      name: plan.name,
      displayName: plan.displayName,
      price: plan.pricing.monthly.price,
      originalPrice: plan.pricing.monthly.price,
      period: 'month',
      description: plan.description,
      popular: plan.name === 'delicious',
      features: getFeatureDisplayText(plan),
      icon: plan.name === 'yummy' ? Utensils : plan.name === 'delicious' ? ChefHat : Crown,
      gradient: plan.name === 'yummy' ? 'from-green-400 to-green-600' :
                plan.name === 'delicious' ? 'from-blue-400 to-blue-600' :
                'from-purple-400 to-purple-600',
      buttonText: plan.isCustomPricing ? 'Contact Sales' :
                  plan.name === 'yummy' ? 'Start with Yummy' :
                  plan.name === 'delicious' ? 'Start 30-Day Free Trial' :
                  'Go Gourmet',
      savings: plan.name === 'delicious' ? 'Most Popular' : undefined
    })) : [];

  const faqs: FAQ[] = [
    {
      question: "Why do you need my credit card for the free trial?",
      answer: "We ask for your card to ensure a seamless experience after your trial. This helps us provide premium support to serious restaurant owners. You won't be charged during your 30-day trial, and you can cancel anytime."
    },
    {
      question: "When will I be charged?",
      answer: "The Delicious plan includes a 30-day free trial. You'll only be charged after the trial ends. We'll send you an email reminder 3 days before your trial expires, giving you time to cancel if you're not completely satisfied. Yummy and Gourmet plans start immediately without a trial."
    },
    {
      question: "How do I cancel my trial?",
      answer: "Canceling is easy - just one click in your dashboard, no phone calls required. You can cancel anytime during your 30-day trial and you won't be charged. We'll even send a reminder email before your trial ends."
    },
    {
      question: "Is there a refund policy?",
      answer: "Yes! If you're not satisfied within the first 30 days after your trial, we offer a 100% money-back guarantee. Just contact our support team and we'll process your refund immediately."
    },
    {
      question: "How does location pricing work?",
      answer: "Each plan includes a certain number of locations. Additional locations can be added for $9.99/month each."
    },
    {
      question: "What kind of support do I get?",
      answer: "All plans include email support. Delicious and Gourmet plans get 24/7 priority support for faster response times."
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      restaurant: "The Garden Bistro",
      text: "Our customers love the dietary filters! We've seen a 40% increase in customer satisfaction since implementing DineAI.",
      rating: 5
    },
    {
      name: "Mike Chen",
      restaurant: "Chen's Asian Fusion",
      text: "The AI-generated images transformed our menu presentation. Every dish looks professional and appetizing!",
      rating: 5
    },
    {
      name: "Lisa Martinez",
      restaurant: "Bella Vista",
      text: "DineAI's analytics showed us which dishes customers search for most. We've optimized our menu based on real data.",
      rating: 5
    }
  ];

  const handleGetStarted = (planId?: string) => {
    const selectedPlan = plansData?.plans.find(p => p.name === planId);
    if (selectedPlan?.isCustomPricing) {
      navigate('/contact');
      return;
    }

    if (planId) {
      localStorage.setItem('selected_plan', planId);
      localStorage.setItem('billing_period', billingPeriod);
    }
    navigate('/restaurant-menu-search');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] flex items-center justify-center">
        <div className="text-white text-xl">Loading plans...</div>
      </div>
    );
  }

  if (error || !plansData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] flex items-center justify-center">
        <div className="text-white text-xl">Failed to load pricing. Please refresh the page.</div>
      </div>
    );
  }

  // Product structured data for AI search engines
  const productStructuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Dine AI Virtual Assistant",
    "description": "AI-powered menu management platform for restaurants. Virtual Assistant digitizes menus, generates AI food images, provides customer analytics, and powers personalized recommendations through our mobile app. It does NOT handle phone orders, reservations, or table bookings - those are separate products (Virtual Host for phone orders).",
    "brand": {
      "@type": "Brand",
      "name": "Dine AI"
    },
    "category": "Restaurant Menu Management Software",
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "USD",
      "lowPrice": "31.99",
      "highPrice": "199",
      "offerCount": "3",
      "offers": [
        {
          "@type": "Offer",
          "name": "Yummy Plan",
          "price": "39.99",
          "priceCurrency": "USD",
          "priceValidUntil": "2025-12-31",
          "availability": "https://schema.org/InStock",
          "description": "1 location, 50 AI images/year, menu digitization, customer analytics"
        },
        {
          "@type": "Offer",
          "name": "Delicious Plan",
          "price": "59.99",
          "priceCurrency": "USD",
          "priceValidUntil": "2025-12-31",
          "availability": "https://schema.org/InStock",
          "description": "2 locations, 150 AI images/year, 30-day free trial, priority support"
        },
        {
          "@type": "Offer",
          "name": "Gourmet Plan",
          "price": "159.99",
          "priceCurrency": "USD",
          "priceValidUntil": "2025-12-31",
          "availability": "https://schema.org/InStock",
          "description": "5 locations, 500 AI images/year, premium support, advanced analytics"
        }
      ]
    },
    "additionalProperty": [
      {
        "@type": "PropertyValue",
        "name": "Primary Function",
        "value": "Digital menu management, AI food image generation, customer analytics"
      },
      {
        "@type": "PropertyValue",
        "name": "NOT Included",
        "value": "Does NOT handle phone orders, reservations, or table bookings"
      },
      {
        "@type": "PropertyValue",
        "name": "Mobile App",
        "value": "Dine AI mobile app for iOS and Android with personalized recommendations"
      }
    ]
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Does Dine AI handle reservations?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. Dine AI Virtual Assistant is a menu management and customer insights platform. It does not handle table reservations, seating, or booking. For reservations, restaurants should use dedicated platforms like OpenTable or Resy."
        }
      },
      ...faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    ]
  };

  // Combined structured data array
  const combinedStructuredData = [productStructuredData, faqStructuredData];

  return (
    <>
      <SEOHead
        title="Virtual Assistant - AI Menu Intelligence | Dine AI"
        description="AI-powered menu management for restaurants. Digitize menus, generate AI food images, get customer analytics. NOT a reservation or phone ordering system. Yummy ($49), Delicious ($99 with trial), Gourmet ($199)."
        keywords="restaurant menu AI, AI menu software, digital menu, AI food images, restaurant analytics, menu intelligence, menu digitization"
        structuredData={combinedStructuredData}
      />
      <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5]">
        <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-block mb-4">
              <span className="bg-white/20 text-white px-4 py-2 rounded-full text-sm font-semibold">
                Virtual Assistant
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white">
              Transform Your Menu with{" "}
              <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                AI Intelligence
              </span>
            </h1>
            <p className="text-xl text-white/80 mb-8 max-w-3xl mx-auto">
              Digitize your menu, generate professional AI images, and delight customers with smart filters and analytics.
              Start free, see results, then choose the plan that fits your growth.
            </p>

            {/* Value Props */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20"
              >
                <Camera className="w-12 h-12 text-dineai-yellow mx-auto mb-4" />
                <h3 className="font-bold mb-2 text-white">AI-Generated Images</h3>
                <p className="text-sm text-white/70">Professional food photos for every menu item</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20"
              >
                <Smartphone className="w-12 h-12 text-dineai-yellow mx-auto mb-4" />
                <h3 className="font-bold mb-2 text-white">Customer Mobile App</h3>
                <p className="text-sm text-white/70">Diners browse your menu with smart dietary filters</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20"
              >
                <BarChart3 className="w-12 h-12 text-dineai-yellow mx-auto mb-4" />
                <h3 className="font-bold mb-2 text-white">Analytics & Insights</h3>
                <p className="text-sm text-white/70">Understand what customers search for most</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards Section */}
      <section id="pricing-plans" className="py-16 bg-[#2256e5]">
        <div className="container mx-auto max-w-6xl px-4">
          {/* Billing Toggle */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex justify-center items-center mb-8"
          >
            <div className="bg-white/10 backdrop-blur-md rounded-full p-1 flex items-center relative">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  billingPeriod === 'monthly'
                    ? 'bg-white text-blue-600'
                    : 'text-white/80 hover:text-white'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod('quarterly')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 relative ${
                  billingPeriod === 'quarterly'
                    ? 'bg-white text-blue-600'
                    : 'text-white/80 hover:text-white'
                }`}
              >
                Quarterly
                {billingPeriod === 'quarterly' && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: -24 }}
                    className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap"
                  >
                    Save 10%
                  </motion.div>
                )}
              </button>
              <button
                onClick={() => setBillingPeriod('annual')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 relative ${
                  billingPeriod === 'annual'
                    ? 'bg-white text-blue-600'
                    : 'text-white/80 hover:text-white'
                }`}
              >
                Annual
                {billingPeriod === 'annual' && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: -24 }}
                    className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap"
                  >
                    Save 20%
                  </motion.div>
                )}
              </button>
            </div>
          </motion.div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
                className={`relative bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-8 hover:bg-white/15 transition-all duration-300 flex flex-col ${
                  plan.popular ? 'ring-2 ring-yellow-400 scale-105' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-yellow-400 text-yellow-900 px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                      <Star className="w-4 h-4" />
                      Most Popular
                    </div>
                  </div>
                )}

                <div className="text-center mb-8">
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${plan.gradient} flex items-center justify-center`}>
                    <plan.icon className="w-8 h-8 text-white" />
                  </div>

                  <h3 className="text-2xl font-bold mb-2 text-white">{plan.displayName}</h3>
                  <p className="text-white/60 text-sm mb-4">{plan.description}</p>

                  <div className="mb-4">
                    {plansData.plans.find(p => p.name === plan.name)?.isCustomPricing ? (
                      <>
                        <span className="text-4xl font-bold text-white">Custom</span>
                        <div className="text-sm text-white/60 mt-2">
                          Contact us for pricing
                        </div>
                      </>
                    ) : billingPeriod === 'annual' && plansData ? (
                      <>
                        <span className="text-4xl font-bold text-white">
                          ${(plansData.plans.find(p => p.name === plan.name)?.pricing.annual.monthlyEquivalent || plan.price * 0.8).toFixed(2)}
                        </span>
                        <span className="text-white/60">/month</span>
                        <div className="text-sm text-white/60 mt-1">
                          <span className="line-through">${plan.price}/mo</span>
                          <span className="text-green-400 ml-2">Save ${((plan.price - (plansData.plans.find(p => p.name === plan.name)?.pricing.annual.monthlyEquivalent || plan.price * 0.8)) * 12).toFixed(0)}/year</span>
                        </div>
                        <div className="text-xs text-white/50 mt-1">
                          Billed ${plansData.plans.find(p => p.name === plan.name)?.pricing.annual.price.toFixed(2)} annually
                        </div>
                      </>
                    ) : billingPeriod === 'quarterly' && plansData ? (
                      <>
                        <span className="text-4xl font-bold text-white">
                          ${(plansData.plans.find(p => p.name === plan.name)?.pricing.quarterly.monthlyEquivalent || plan.price * 0.9).toFixed(2)}
                        </span>
                        <span className="text-white/60">/month</span>
                        <div className="text-sm text-white/60 mt-1">
                          <span className="line-through">${plan.price}/mo</span>
                          <span className="text-orange-400 ml-2">Save ${((plan.price - (plansData.plans.find(p => p.name === plan.name)?.pricing.quarterly.monthlyEquivalent || plan.price * 0.9)) * 3).toFixed(0)}/quarter</span>
                        </div>
                        <div className="text-xs text-white/50 mt-1">
                          Billed ${plansData.plans.find(p => p.name === plan.name)?.pricing.quarterly.price || (plan.price * 3 * 0.9).toFixed(2)} every 3 months
                        </div>
                      </>
                    ) : (
                      <>
                        <span className="text-4xl font-bold text-white">${plan.price}</span>
                        <span className="text-white/60">/month</span>
                      </>
                    )}
                  </div>
                </div>

                <ul className="space-y-3 mb-8 flex-grow">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-white/80 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleGetStarted(plan.id)}
                  className={`w-full py-3 font-semibold rounded-xl transition-all duration-300 ${
                    plan.popular
                      ? 'bg-yellow-400 hover:bg-yellow-300 text-yellow-900 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                      : 'bg-white/20 hover:bg-white/30 text-white border border-white/30 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                  }`}
                >
                  {plan.buttonText}
                </Button>
              </motion.div>
            ))}
          </div>

          {/* Additional Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="mt-12 text-center"
          >
            <p className="text-white/80 mb-4">
              Looking for AI phone ordering? Check out{" "}
              <a href="/products/virtual-host" className="text-dineai-yellow hover:underline font-semibold">
                Virtual Host
              </a>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-[#2256e5]">
        <div className="container mx-auto max-w-6xl px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              Loved by{" "}
              <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                Restaurant Owners
              </span>
            </h2>
            <p className="text-xl text-white/80">
              See what our customers have to say
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-white/90 mb-4 italic">"{testimonial.text}"</p>
                <div>
                  <p className="font-semibold text-white">{testimonial.name}</p>
                  <p className="text-sm text-white/70">{testimonial.restaurant}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-[#1f4ed1]">
        <div className="container mx-auto max-w-4xl px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-white/80">
              Everything you need to know about Virtual Assistant
            </p>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.05 }}
                className="bg-white/10 backdrop-blur-md rounded-xl border border-white/20 overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-white/5 transition-colors"
                >
                  <span className="font-semibold text-white">{faq.question}</span>
                  {expandedFAQ === index ? (
                    <ChevronUp className="w-5 h-5 text-white/60" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-white/60" />
                  )}
                </button>
                {expandedFAQ === index && (
                  <div className="px-6 pb-4">
                    <p className="text-white/80">{faq.answer}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-[#2256e5]">
        <div className="container mx-auto max-w-4xl px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              Ready to Transform Your Menu?
            </h2>
            <p className="text-xl text-white/80 mb-8">
              Start your AI-powered restaurant transformation today
            </p>
            <Button
              onClick={() => handleGetStarted()}
              size="lg"
              className="bg-dineai-yellow hover:bg-dineai-yellow/90 text-black px-12 py-4 text-lg font-bold rounded-xl transition-all duration-300 transform hover:scale-105"
            >
              Sign Up Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
    </>
  );
};

export default VirtualAssistant;
